import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";
import ChatPanel from "./ChatPanel";
import useChatSession from "./useChatSession";
import { cn } from "@/lib/utils";

const ChatWidget = () => {
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);

    const {
        sessionId,
        messages,
        handoffService,
        sending,
        sessionStatus,
        error,
        sendMessage,
        clearSession,
    } = useChatSession({ initialPage: pathname });

    // Reset state when the route changes — keep the widget closed by default
    // and don't surface old hand-off CTAs on a new page.
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    const handleToggle = () => setOpen((v) => !v);

    return (
        <>
            {/* Launcher button — pinned bottom-right; lifted up on mobile so it
                sits above the MobileBottomBar and the BlogStickyCta strip. */}
            <button
                type="button"
                onClick={handleToggle}
                className={cn(
                    "fixed z-[55] inline-flex items-center justify-center transition-all duration-200 ease-out",
                    "bottom-[136px] right-4 md:bottom-6 md:right-6",
                    "h-14 w-14 bg-brand-dark text-white border-2 border-brand-cyan/40 hover:border-brand-cyan shadow-lg",
                    open && "opacity-0 pointer-events-none scale-90",
                )}
                aria-label={open ? "Close chat" : "Open chat"}
                aria-expanded={open}
                data-testid="chat-launcher-button"
            >
                {open ? (
                    <X className="h-6 w-6" strokeWidth={2.25} />
                ) : (
                    <span className="relative">
                        <MessageCircle className="h-6 w-6 text-brand-cyan" strokeWidth={2.25} />
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-brand-coral animate-pulse" />
                    </span>
                )}
            </button>

            <ChatPanel
                open={open}
                onClose={() => setOpen(false)}
                messages={messages}
                sending={sending}
                sessionStatus={sessionStatus}
                error={error}
                handoffService={handoffService}
                sessionId={sessionId}
                onSend={sendMessage}
                onClear={clearSession}
            />
        </>
    );
};

export default ChatWidget;
